import { useEffect, useState } from "react";
import { TokenService } from "../lib/tokenService";
import { setShortSsoToken, setBaseBearer } from "../lib/toolsSso";
import { sendToTerminal } from "../lib/terminalLogger";
import { extractInitialToken } from "../lib/initialHash";
import {
	handleBootstrapData,
	type BootstrapData,
} from "../lib/corel/bootstrapHandler";
import { api } from "../lib/api";
import { validateMeResponse } from "../lib/auth/validators";

/**
 * Extrai o campo `exp` do payload JWT e converte para ISO string.
 */
function extractExpiresAtFromJwt(token: string): string | undefined {
	try {
		const parts = token.split(".");
		if (parts.length < 2) return undefined;
		const payload = parts[1];
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		const obj = JSON.parse(json);
		if (typeof obj?.exp === "number") {
			return new Date(obj.exp * 1000).toISOString();
		}
	} catch {
		// Silencioso
	}
	return undefined;
}

/**
 * LÃª o token curto do fragmento (#token=...) ao abrir o Docker 2,
 * persiste o token, carrega dados mÃ­nimos do usuÃ¡rio e permissÃµes,
 * remove o fragmento da URL e dispara 'auth:updated'.
 *
 * âœ… OTIMIZADO:
 * - Removida validaÃ§Ã£o duplicada (checkToken jÃ¡ executado em openToolsDocker)
 * - Timeout de fallback reduzido de 500ms para 100ms
 * - Busca de dados da API em paralelo (Promise.all)
 * - Apenas 1 tentativa (vs 2 antes)
 */
export function useSsoFragmentLogin() {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);
	const ranFlagKey = "__ssoFragmentHandled";

	useEffect(() => {
		// Evitar execuÃ§Ãµes duplicadas
		if (done) return;
		try {
			if ((window as any)[ranFlagKey]) return;
		} catch {}
		try {
			(window as any)[ranFlagKey] = true;
		} catch {}

		// Extrair token do hash/search INICIAL (antes do React limpar)
		const { token: extractedToken, ud } = extractInitialToken();
		const token = extractedToken;

		// Se nÃ£o hÃ¡ token SSO do fragmento/query, aguardar e tentar fallback
		if (!token) {
			// âœ… OTIMIZAÃ‡ÃƒO: Timeout reduzido de 500ms para 100ms
			const retryTimeout = setTimeout(() => {
				const existingToken = TokenService.getAccessToken();

				if (existingToken) {
					try {
						setBaseBearer(existingToken);
						// Extrair expiresAt do JWT para agendar auto-refresh corretamente
						const expiresAt = extractExpiresAtFromJwt(existingToken);
						setShortSsoToken(existingToken, expiresAt);
						TokenService.setTokens(existingToken);
						setDone(true);
					} catch {
						// Silencioso - erro jÃ¡ serÃ¡ tratado pelo sistema
					}
				}
			}, 100);

			return () => clearTimeout(retryTimeout);
		}

		setLoading(true);
		(async () => {
			try {
				// Processar dados mÃ­nimos do fragmento usando handler centralizado
				const decodeJwt = (t: string): any => {
					try {
						const [, payload] = t.split(".") as [string, string, string];
						const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
						return JSON.parse(json);
					} catch {
						return null;
					}
				};

				const claims = decodeJwt(token);
				let bootstrapData: BootstrapData = {
					accessToken: token,
				};

				// Se veio userData mÃ­nimo no fragmento, usar ele
				if (ud) {
					try {
						const json = atob(ud.replace(/-/g, "+").replace(/_/g, "/"));
						const parsed = JSON.parse(json);
						bootstrapData = {
							accessToken: token,
							user: parsed.usuario
								? {
										id: parsed.usuario.id,
										nome: parsed.usuario.nome,
										login: parsed.usuario.login,
									}
								: undefined,
							permissoes: parsed.permissions || [],
							permissionsConfig: parsed.permissionsConfig || [],
						};
					} catch {
						// Se falhar, usar claims do JWT
						if (claims) {
							bootstrapData = {
								accessToken: token,
								user: {
									id: Number(claims.sub) || 0,
									nome: String(claims.user || ""),
									login: String(claims.user || ""),
								},
								permissoes: [],
								permissionsConfig: [],
							};
						}
					}
				} else if (claims) {
					// Usar claims do JWT se nÃ£o houver userData no fragmento
					bootstrapData = {
						accessToken: token,
						user: {
							id: Number(claims.sub) || 0,
							nome: String(claims.user || ""),
							login: String(claims.user || ""),
						},
						permissoes: [],
						permissionsConfig: [],
					};
				}

				// Usar handler centralizado para processar bootstrap
				const bootstrapResult = await handleBootstrapData(
					bootstrapData,
					"fragment",
				);
				if (!bootstrapResult.success) {
					throw new Error(
						bootstrapResult.error ||
							"Falha ao processar bootstrap do fragmento",
					);
				}

				// âœ… OTIMIZAÃ‡ÃƒO: Removido checkToken() duplicado
				// O token jÃ¡ foi validado em openToolsDocker() antes de abrir o Docker 2
				// Isso economiza ~100-500ms de latÃªncia

				// Buscar dados completos do usuÃ¡rio (permissÃµes + permissionsConfig) via GET /auth/me (timeout 5s)
				// O bootstrap do fragmento/ud NÃƒO contÃ©m permissÃµes â€” precisamos buscÃ¡-las da API
				try {
					const meResponse = await Promise.race([
						api("/auth/me", { method: "GET" }),
						new Promise<never>((_, reject) =>
							setTimeout(
								() => reject(new Error("Timeout 5s ao buscar /auth/me")),
								5000,
							),
						),
					]);

					const validation = validateMeResponse(meResponse, token);
					if (validation.success) {
						TokenService.setUserData(validation.normalized);
						try {
							window.dispatchEvent(
								new CustomEvent("auth:updated", {
									detail: { userData: validation.normalized },
								}),
							);
						} catch {}

						sendToTerminal(
							"âœ… [SSO] Dados do usuÃ¡rio carregados via /auth/me",
							{
								userId: validation.normalized.data.usuario.id,
								permissionsConfigCount:
									validation.normalized.data.permissionsConfig?.length || 0,
							},
						);
					} else {
						sendToTerminal(
							"âš ï¸ [SSO] Resposta /auth/me invÃ¡lida, usando bootstrap",
							{
								error: validation.error,
							},
						);
					}
				} catch (apiErr) {
					sendToTerminal(
						"âš ï¸ [SSO] Falha ao buscar /auth/me, usando dados do bootstrap",
						{
							error: apiErr instanceof Error ? apiErr.message : String(apiErr),
						},
					);
				}

				// Limpar token e userData da URL (tanto fragmento quanto query string)
				const cleanUrl =
					window.location.pathname +
					window.location.search
						.replace(/[?&]token=[^&#&]+(&|$)/g, (_m, end) =>
							end === "&" ? "&" : "",
						)
						.replace(/[?&]ud=[^&#&]+(&|$)/g, (_m, end) =>
							end === "&" ? "&" : "",
						)
						.replace(/[?&]mode=[^&#&]+(&|$)/g, (_m, end) =>
							end === "&" ? "&" : "",
						)
						.replace(/^\?&/, "?")
						.replace(/\?$/, "");

				try {
					window.history.replaceState({}, document.title, cleanUrl || "/tools");
				} catch {}
				setDone(true);
			} catch (e: any) {
				const errorMsg = e?.message || "Falha ao autenticar com token curto";
				setError(errorMsg);
				try {
					window.dispatchEvent(
						new CustomEvent("sso:init-failed", {
							detail: {
								reason: errorMsg,
								reasonCode: "sso_token_failed",
								severity: "warning",
								expected: false,
							},
						}),
					);
				} catch {}
			} finally {
				setLoading(false);
			}
		})();
	}, [done]);

	return { loading, error, done };
}
